import { useScroll } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Logo } from '@components/Logo';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

interface HeaderProps {
  showNavigation?: boolean;
  showBackButton?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  showNavigation = true,
  showBackButton = false,
}) => {
  // Get current location for active navigation highlighting
  const location = useLocation();

  // Scroll-based animations
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if Tools should be highlighted as active
  const isToolsActive =
    location.pathname === '/tools' || location.pathname.startsWith('/tools/');

  // Check if About Us should be highlighted as active
  const isAboutActive = location.pathname === '/about';

  // Check if Contact should be highlighted as active
  const isContactActive = location.pathname === '/contact';

  // Check if Services should be highlighted as active
  const isServicesActive = location.pathname === '/services';

  // Check if Guides should be highlighted as active
  const isGuidesActive =
    location.pathname === '/guides' || location.pathname.startsWith('/guides/');

  // Track scroll position for header effects
  useEffect(() => {
    const unsubscribe = scrollY.onChange(latest => {
      setIsScrolled(latest > 50);
      // Close mobile menu when scrolling
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    });

    return () => unsubscribe();
  }, [scrollY, isMobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as Element;
        if (
          !target.closest('header') &&
          !target.closest('[data-mobile-menu]')
        ) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-0.5 sm:py-1">
            {/* Logo Section */}
            <div className="flex items-center">
              <a href="/" className="flex items-center">
                <Logo
                  variant="plus"
                  width={120}
                  height={48}
                  className="sm:w-40 sm:h-[73px]"
                />
              </a>
            </div>

            {/* Mobile Menu Button */}
            {showNavigation && (
              <button
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                style={{ color: COLORS.charcoal }}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            )}

            {/* Back Button */}
            {showBackButton && (
              <nav className="flex items-center">
                <a
                  href="/"
                  className="text-sm lg:text-base font-medium transition-all duration-300 hover:scale-105 px-4 py-2 rounded-lg hover:bg-gray-100"
                  style={{ color: COLORS.charcoal }}
                >
                  ← Back to home
                </a>
              </nav>
            )}

            {/* Desktop Navigation */}
            {showNavigation && (
              <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
                <a
                  href="/services"
                  className={`relative text-sm lg:text-base font-medium transition-all duration-300 hover:scale-105 px-3 py-2 rounded-lg group ${
                    isServicesActive
                      ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25'
                      : 'hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  style={{
                    color: isServicesActive ? '#FFFFFF' : COLORS.charcoal,
                  }}
                >
                  <span className="relative z-10">Services</span>
                  {isServicesActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg animate-pulse opacity-75"></div>
                  )}
                </a>
                <a
                  href="/tools"
                  className={`relative text-sm lg:text-base font-medium transition-all duration-300 hover:scale-105 px-3 py-2 rounded-lg group ${
                    isToolsActive
                      ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25'
                      : 'hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  style={{
                    color: isToolsActive ? '#FFFFFF' : COLORS.charcoal,
                  }}
                >
                  <span className="relative z-10">Tools</span>
                  {isToolsActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg animate-pulse opacity-75"></div>
                  )}
                </a>
                <a
                  href="/guides"
                  className={`relative text-sm lg:text-base font-medium transition-all duration-300 hover:scale-105 px-3 py-2 rounded-lg group ${
                    isGuidesActive
                      ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25'
                      : 'hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  style={{
                    color: isGuidesActive ? '#FFFFFF' : COLORS.charcoal,
                  }}
                >
                  <span className="relative z-10">Guides</span>
                  {isGuidesActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg animate-pulse opacity-75"></div>
                  )}
                </a>
                <a
                  href="/about"
                  className={`relative text-sm lg:text-base font-medium transition-all duration-300 hover:scale-105 px-3 py-2 rounded-lg group ${
                    isAboutActive
                      ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25'
                      : 'hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  style={{
                    color: isAboutActive ? '#FFFFFF' : COLORS.charcoal,
                  }}
                >
                  <span className="relative z-10">About</span>
                  {isAboutActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg animate-pulse opacity-75"></div>
                  )}
                </a>
                <a
                  href="/contact"
                  className={`relative text-sm lg:text-base font-medium transition-all duration-300 hover:scale-105 px-3 py-2 rounded-lg group ${
                    isContactActive
                      ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25'
                      : 'hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  style={{
                    color: isContactActive ? '#FFFFFF' : COLORS.charcoal,
                  }}
                >
                  <span className="relative z-10">Contact</span>
                  {isContactActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg animate-pulse opacity-75"></div>
                  )}
                </a>
                <a
                  href={`${CONTACT_INFO.social.website}/client-login`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm lg:text-base font-medium transition-all duration-300 hover:scale-105 px-3 py-2 rounded-lg hover:bg-gray-50 hover:text-blue-600"
                  style={{
                    color: COLORS.charcoal,
                  }}
                >
                  <span className="relative z-10">Client login</span>
                </a>
              </nav>
            )}
          </div>
        </div>

        {/* Bottom border */}
        <div
          className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent transition-opacity duration-300 ${
            isScrolled ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </header>

      {/* Mobile Menu Dropdown */}
      {showNavigation && isMobileMenuOpen && (
        <div
          data-mobile-menu
          className="fixed top-12 sm:top-22 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200/50 shadow-lg"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col space-y-4">
              <a
                href="/services"
                className={`relative text-base font-medium py-3 px-4 rounded-xl transition-all duration-300 group ${
                  isServicesActive
                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25 scale-105'
                    : 'hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-blue-600 hover:scale-102'
                }`}
              >
                <span className="relative z-10">Services</span>
                {isServicesActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl opacity-90 animate-pulse"></div>
                )}
              </a>
              <a
                href="/tools"
                className={`relative text-base font-medium py-3 px-4 rounded-xl transition-all duration-300 group ${
                  isToolsActive
                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25 scale-105'
                    : 'hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-blue-600 hover:scale-102'
                }`}
              >
                <span className="relative z-10">Tools</span>
                {isToolsActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl opacity-90 animate-pulse"></div>
                )}
              </a>
              <a
                href="/guides"
                className={`relative text-base font-medium py-3 px-4 rounded-xl transition-all duration-300 group ${
                  isGuidesActive
                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25 scale-105'
                    : 'hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-blue-600 hover:scale-102'
                }`}
              >
                <span className="relative z-10">Guides</span>
                {isGuidesActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl opacity-90 animate-pulse"></div>
                )}
              </a>
              <a
                href="/about"
                className={`relative text-base font-medium py-3 px-4 rounded-xl transition-all duration-300 group ${
                  isAboutActive
                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25 scale-105'
                    : 'hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-blue-600 hover:scale-102'
                }`}
              >
                <span className="relative z-10">About Us</span>
                {isAboutActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl opacity-90 animate-pulse"></div>
                )}
              </a>
              <a
                href="/contact"
                className={`relative text-base font-medium py-3 px-4 rounded-xl transition-all duration-300 group ${
                  isContactActive
                    ? 'text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25 scale-105'
                    : 'hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-blue-600 hover:scale-102'
                }`}
              >
                <span className="relative z-10">Contact</span>
                {isContactActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl opacity-90 animate-pulse"></div>
                )}
              </a>
              <a
                href={`${CONTACT_INFO.social.website}/client-login`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-medium py-3 px-4 rounded-xl transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-blue-600 hover:scale-102"
                style={{
                  color: COLORS.charcoal,
                }}
              >
                <span className="relative z-10">Client login</span>
              </a>
            </div>
          </nav>
        </div>
      )}

      {/* Spacer */}
      <div className="h-12 sm:h-22"></div>
    </>
  );
};

export default Header;
